import { ReactNode } from 'react';

import { RowSelectionState } from '@tanstack/react-table';

import { cn } from '@/lib/utils';

import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';

interface TablePageGridViewProps<T> {
  data: T[];
  notFoundDisplay?: ReactNode;
  containerClassName?: string;
  loading?: boolean;
  ItemComponent: React.ComponentType<{ item: T; handleSelectedItemsChange: (index: number) => void; index: number }>;
  selectedItems: RowSelectionState;
  onSelectedItemsChange: (selectedItems: RowSelectionState) => void;
  onItemClick?: (item: T) => void;
  cornerFold?: boolean;
  itemClassName?: string;
}

function GridItemSkeleton({ cornerFold, itemClassName }: { cornerFold: boolean; itemClassName?: string }) {
  return (
    <div className={cn('group relative w-[155px] outline-none', itemClassName)}>
      <div className="relative h-full" style={{ filter: 'drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.2))' }}>
        <div
          className="pointer-events-none absolute -top-[3px] -left-[3px] z-[2] h-[calc(100%+6px)] w-[calc(100%+6px)] rounded-[9px]"
          style={{
            clipPath: cornerFold ? 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)' : undefined,
          }}
        />
        <div
          className="relative z-[3] h-full overflow-hidden rounded-md bg-white"
          style={{
            clipPath: cornerFold ? 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)' : undefined,
          }}>
          {cornerFold && <div className="absolute right-0 bottom-0 h-[20px] w-[20px]" style={{ boxShadow: '0 0 6px rgba(0, 0, 0, 0.2)' }} />}
          <div className="pointer-events-none absolute right-0 bottom-0 z-10 h-[20px] w-[20px] border-[3px] border-transparent" />
          <div className="flex flex-col gap-2 p-2">
            <div className="flex w-full justify-between">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-4" />
            </div>
            <div className="flex flex-col gap-2">
              <div>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="mt-1 h-3 w-1/4" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
            {cornerFold && <div className="h-[20px]" />}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TablePageGridView<T>({
  data,
  notFoundDisplay,
  containerClassName,
  loading = false,
  ItemComponent,
  selectedItems,
  onSelectedItemsChange,
  onItemClick,
  cornerFold = true,
  itemClassName,
}: TablePageGridViewProps<T>) {
  function handleSelectedItemsChange(index: number) {
    if (selectedItems[index] === true) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [index]: _, ...rest } = selectedItems;
      onSelectedItemsChange(rest);
    } else {
      onSelectedItemsChange({ ...selectedItems, [index]: true });
    }
  }

  function handleSelectAll() {
    if (Object.keys(selectedItems).length === data.length) {
      onSelectedItemsChange({});
    } else {
      onSelectedItemsChange(data.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}));
    }
  }

  return (
    <div className="border-t">
      <div className="h-full w-full max-w-[1380px]">
        <div className={cn('flex h-full flex-col gap-4 overflow-y-auto p-5', containerClassName)}>
          {!loading && data.length === 0 && notFoundDisplay}

          {!loading && data.length > 0 && (
            <Label className="text-sm font-medium">
              <Checkbox checked={Object.keys(selectedItems).length === data.length} onCheckedChange={handleSelectAll} />
              <span className="ml-2 font-normal">Select all</span>
            </Label>
          )}

          <div className="flex flex-wrap gap-4">
            {loading
              ? Array.from({ length: 8 }).map((_, index) => <GridItemSkeleton key={index} cornerFold={cornerFold} itemClassName={itemClassName} />)
              : data.map((item, index) => {
                  const isSelected = selectedItems[index] === true;

                  return (
                    <div
                      key={index}
                      className={cn('group relative w-[155px] cursor-pointer outline-none', itemClassName)}
                      tabIndex={0}
                      onClick={() => onItemClick?.(item)}>
                      {/* Shadow wrapper */}
                      <div
                        className="relative h-full"
                        style={{
                          filter: 'drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.2))',
                        }}>
                        {/* Background with border effect */}
                        <div
                          className={cn(
                            'group-focus-visible:bg-default-blue/50 pointer-events-none absolute -top-[3px] -left-[3px] z-[2] h-[calc(100%+6px)] w-[calc(100%+6px)] rounded-[9px] outline-none',
                            isSelected && 'bg-default-blue',
                          )}
                          style={{
                            clipPath: cornerFold ? 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)' : undefined,
                          }}
                        />

                        {/* Content wrapper */}
                        <div
                          className={cn(
                            'hover:bg-grey-1 relative z-[3] h-full cursor-pointer overflow-hidden rounded-md bg-white transition duration-200',
                            isSelected && '!bg-default-light-blue',
                          )}
                          style={{
                            clipPath: cornerFold ? 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)' : undefined,
                          }}>
                          {/* Shadow for folded corner */}
                          {cornerFold && <div className="absolute right-0 bottom-0 h-[20px] w-[20px]" style={{ boxShadow: '0 0 6px rgba(0, 0, 0, 0.2)' }} />}

                          {/* Folded corner border */}
                          {cornerFold && (
                            <div
                              className={cn(
                                'group-focus-visible:border-default-blue/50 pointer-events-none absolute right-0 bottom-0 z-10 h-[20px] w-[20px] border-[3px] border-transparent',
                                isSelected && 'border-default-blue',
                              )}
                            />
                          )}

                          {/* Component content */}
                          <div>
                            <ItemComponent item={item} handleSelectedItemsChange={handleSelectedItemsChange} index={index} />

                            {/* Space */}
                            {cornerFold && <div className="h-[20px]" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
      </div>
    </div>
  );
}
